import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SimpleUser } from 'sip.js/lib/platform/web';
import { extensionsAPI, cdrAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';


const WebPhone = () => {
  const { user } = useAuth();
  const [serverUrl, setServerUrl] = useState('wss://connect01.raicotech.cl:8090/ws');
  const [extension, setExtension] = useState('');
  const [password, setPassword] = useState('');
  const [domain, setDomain] = useState('');
  const [regState, setRegState] = useState('unregistered');
  const [regError, setRegError] = useState('');

  const [callState, setCallState] = useState('idle');
  const [dialNumber, setDialNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [isHeld, setIsHeld] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const [incomingNumber, setIncomingNumber] = useState('');

  const [recentCalls, setRecentCalls] = useState([]);

  const [recentNumbers, setRecentNumbers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('webphone_recent') || '[]');
    } catch {
      return [];
    }
  });

  const simpleUserRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const timerRef = useRef(null);
  const dialInputRef = useRef(null);
  const hangupSourceRef = useRef(null);
  const ringtoneRef = useRef(null);

  const extractDomain = useCallback((url) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname;
    } catch {
      return '';
    }
  }, []);

  const fetchRecentCalls = useCallback(async (ext) => {
    if (!ext) return;
    try {
      const data = await cdrAPI.getCDRs({ src: ext, limit: 10 });
      if (Array.isArray(data)) setRecentCalls(data);
    } catch (_) {}
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, [clearTimer]);

  const resetCallState = useCallback(() => {
    setCallState('idle');
    setIncomingNumber('');
    setIsHeld(false);
    setIsMuted(false);
    clearTimer();
    setCallDuration(0);
  }, [clearTimer]);

  const doRegister = useCallback(async (srv, ext, pwd, dom) => {
    setRegState('connecting');
    setRegError('');
    setServerUrl(srv);
    setExtension(ext);
    setPassword(pwd);
    setDomain(dom);

    try {
      const aor = `sip:${ext}@${dom}`;
      const audioEl = remoteAudioRef.current;

      const simpleUser = new SimpleUser(srv, {
        aor,
        userAgentOptions: {
          authorizationUsername: ext,
          authorizationPassword: pwd,
          logLevel: 'warn',
        },
        delegate: {
          onCallReceived: () => {
            const session = simpleUserRef.current?.session;
            if (session) {
              const remote = session.remoteIdentity?.uri?.user || 'Unknown';
              setIncomingNumber(remote);
            }
            setCallState('ringing');
            extensionsAPI.callRinging({ extension: ext, status: 'RINGING', username: user?.username }).catch(err => console.error('Failed to notify call ringing:', err));
          },
          onCallAnswered: () => {
            setCallState('connected');
            startTimer();
            extensionsAPI.callAnswer({ extension: ext, status: 'ANSWER', username: user?.username }).catch(err => console.error('Failed to notify call answer:', err));
          },
          onCallHangup: () => {
            resetCallState();
            const source = hangupSourceRef.current;
            hangupSourceRef.current = null;
            const hangupStatus = source === 'agent' ? 'AGENT-HANGUP' : 'CALLER-HANGUP';
            extensionsAPI.callHangup({ extension: ext, status: hangupStatus, username: user?.username }).catch(err => console.error('Failed to notify call hangup:', err));
          },
          onCallHold: (held) => {
            setIsHeld(held);
          },
          onRegistered: () => {
            setRegState('registered');
          },
          onUnregistered: () => {
            setRegState('unregistered');
          },
          onServerDisconnect: (error) => {
            if (error) {
              setRegState('error');
              setRegError(error.message || 'Disconnected');
            } else {
              setRegState('unregistered');
            }
          },
        },
        media: {
          remote: {
            audio: audioEl || undefined,
          },
        },
      });

      simpleUserRef.current = simpleUser;

      await simpleUser.connect();
      setRegState('registering');
      await simpleUser.register();
      setRegState('registered');
      setDialNumber('');
      resetCallState();
      localStorage.setItem('webphone_creds', JSON.stringify({ serverUrl: srv, extension: ext, password: pwd, domain: dom }));
      extensionsAPI.register({ extension: ext, status: 'REGISTER', username: user?.username, ip_address: '', user_agent: 'WebPhone' }).catch(err => console.error('Failed to notify extension registration:', err));
      fetchRecentCalls(ext);
      setTimeout(() => dialInputRef.current?.focus(), 300);
    } catch (error) {
      setRegState('error');
      setRegError(error.message || 'Registration failed');
    }
  }, [startTimer, resetCallState]);

  const handleRegister = async () => {
    const dom = domain || extractDomain(serverUrl);
    if (!serverUrl || !extension || !password || !dom) {
      setRegError('Please fill in all fields (Server URL, Extension, Password)');
      return;
    }
    await doRegister(serverUrl, extension, password, dom);
  };

  const handleUnregister = async (clearCreds = true) => {
    try {
      if (simpleUserRef.current) {
        simpleUserRef.current.delegate = undefined;
        await simpleUserRef.current.unregister().catch(() => {});
        simpleUserRef.current.disconnect().catch(() => {});
        simpleUserRef.current = null;
      }
    } catch (error) {
      console.error('Unregister error:', error);
    }
    extensionsAPI.unregister({ extension: extension, status: 'UNREGISTER', username: user?.username, ip_address: '', user_agent: 'WebPhone' }).catch(err => console.error('Failed to notify extension unregistration:', err));
    if (clearCreds) {
      localStorage.removeItem('webphone_creds');
    }
    setRegState('unregistered');
    resetCallState();
  };

  const handleDial = (digit) => {
    setDialNumber(prev => prev + digit);
  };

  const handleClear = () => {
    setDialNumber('');
    dialInputRef.current?.focus();
  };

  const handleBackspace = () => {
    setDialNumber(prev => prev.slice(0, -1));
  };

  const handleInputChange = (e) => {
    setDialNumber(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCall();
    }
  };

  const saveRecentNumber = (number) => {
    if (!number) return;
    setRecentNumbers(prev => {
      const filtered = prev.filter(n => n !== number);
      const updated = [number, ...filtered].slice(0, 20);
      localStorage.setItem('webphone_recent', JSON.stringify(updated));
      return updated;
    });
  };

  const handleCall = async () => {
    if (!dialNumber || !simpleUserRef.current || regState !== 'registered') return;

    try {
      const dom = domain || extractDomain(serverUrl);
      const target = `sip:${dialNumber}@${dom}`;
      setCallState('calling');
      saveRecentNumber(dialNumber);
      await simpleUserRef.current.call(target);
    } catch (error) {
      console.error('Call error:', error);
      setCallState('idle');
    }
  };

  const handleHangup = async () => {
    hangupSourceRef.current = 'agent';
    try {
      if (simpleUserRef.current) {
        await simpleUserRef.current.hangup();
      }
    } catch (error) {
      console.error('Hangup error:', error);
    }
    resetCallState();
  };

  const handleAnswer = async () => {
    try {
      if (simpleUserRef.current) {
        await simpleUserRef.current.answer();
      }
    } catch (error) {
      console.error('Answer error:', error);
      resetCallState();
    }
  };

  const handleDecline = async () => {
    try {
      if (simpleUserRef.current) {
        await simpleUserRef.current.decline();
      }
    } catch (error) {
      console.error('Decline error:', error);
    }
    resetCallState();
  };

  const handleHold = async () => {
    try {
      if (simpleUserRef.current) {
        if (isHeld) {
          await simpleUserRef.current.unhold();
        } else {
          await simpleUserRef.current.hold();
        }
      }
    } catch (error) {
      console.error('Hold error:', error);
    }
  };

  const handleMute = () => {
    if (simpleUserRef.current) {
      if (isMuted) {
        simpleUserRef.current.unmute();
        setIsMuted(false);
      } else {
        simpleUserRef.current.mute();
        setIsMuted(true);
      }
    }
  };

  const handleSendDTMF = async (tone) => {
    try {
      if (simpleUserRef.current && callState === 'connected') {
        await simpleUserRef.current.sendDTMF(tone);
      }
    } catch (error) {
      console.error('DTMF error:', error);
    }
  };

  const handleDigitClick = (digit) => {
    if (callState === 'connected') {
      handleSendDTMF(digit);
    } else {
      handleDial(digit);
    }
  };




  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    const saved = localStorage.getItem('webphone_creds');
    if (saved) {
      try {
        const creds = JSON.parse(saved);
        setServerUrl(creds.serverUrl);
        setExtension(creds.extension);
        setPassword(creds.password);
        setDomain(creds.domain || '');
      } catch {}
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('webphone_creds');
    if (saved) {
      try {
        const creds = JSON.parse(saved);
        if (creds.serverUrl && creds.extension && creds.password) {
          doRegister(creds.serverUrl, creds.extension, creds.password, creds.domain || '');
        }
      } catch {}
    }
  }, [doRegister]);

  useEffect(() => {
    if (regState !== 'registered' || !extension) return;
    const interval = setInterval(() => fetchRecentCalls(extension), 30000);
    return () => clearInterval(interval);
  }, [regState, extension, fetchRecentCalls]);

  useEffect(() => {
    const saved = localStorage.getItem('webphone_creds');
    let savedExt = '';
    if (saved) {
      try { savedExt = JSON.parse(saved).extension || ''; } catch {}
    }

    const handleBeforeUnload = () => {
      const ext = savedExt || extension;
      if (ext) {
        extensionsAPI.unregister({ extension: ext, status: 'UNREGISTER', username: user?.username, ip_address: '', user_agent: 'WebPhone' }).catch(() => {});
      }
      if (simpleUserRef.current) {
        simpleUserRef.current.delegate = undefined;
        simpleUserRef.current.unregister().catch(() => {});
        simpleUserRef.current.disconnect().catch(() => {});
        simpleUserRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
      handleUnregister(false);
      clearTimer();
    };
  }, [clearTimer, extension]);

  useEffect(() => {
    if (callState === 'ringing') {
      const audio = new Audio('/ringtone.wav');
      audio.loop = true;
      audio.volume = 0.5;
      audio.play().catch(err => console.error('Failed to play ringtone:', err));
      ringtoneRef.current = audio;
    } else if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }
  }, [callState]);

  const isCallActive = callState === 'connected';

  return (
    <div style={styles.container}>
      <div style={styles.statusIndicator}>
        <span style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          display: 'inline-block',
          backgroundColor: regState === 'registered' ? '#27ae60' : '#e74c3c',
          boxShadow: `0 0 6px ${regState === 'registered' ? '#27ae60' : '#e74c3c'}`,
        }} />
      </div>
      <h1 style={styles.pageTitle}>WebPhone</h1>

      <div style={styles.layout}>
        <div style={styles.panel}>
          <h2 style={styles.panelTitle}>SIP Registration</h2>

          <div style={styles.formGroup}>
            <label style={styles.label}>Server URL</label>
            <input
              type="text"
              placeholder="wss://pbx.example.com:8089/ws"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              style={styles.input}
              disabled={regState === 'registered' || regState === 'connecting' || regState === 'registering'}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Extension</label>
            <input
              type="text"
              placeholder="1001"
              value={extension}
              onChange={(e) => setExtension(e.target.value)}
              style={styles.input}
              disabled={regState === 'registered' || regState === 'connecting' || regState === 'registering'}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRegister(); }}
              style={styles.input}
              disabled={regState === 'registered' || regState === 'connecting' || regState === 'registering'}
            />
          </div>

          <div style={styles.statusBar}>
            <span style={{
              ...styles.statusDot,
              backgroundColor:
                regState === 'registered' ? '#27ae60' :
                regState === 'error' ? '#e74c3c' :
                (regState === 'connecting' || regState === 'registering') ? '#f39c12' : '#6b7280'
            }} />
            <span style={styles.statusText}>
              {regState === 'registered' ? 'Registered' :
               regState === 'registering' ? 'Registering...' :
               regState === 'connecting' ? 'Connecting...' :
               regState === 'error' ? `Error: ${regError}` :
               'Not Registered'}
            </span>
          </div>

          {regState === 'registered' ? (
            <button onClick={() => handleUnregister(true)} style={styles.btnDanger}>
              Unregister
            </button>
          ) : (
            <button
              onClick={handleRegister}
              style={styles.btnPrimary}
              disabled={regState === 'connecting' || regState === 'registering'}
            >
              {regState === 'connecting' ? 'Connecting...' :
               regState === 'registering' ? 'Registering...' : 'REGISTER'}
            </button>
          )}
        </div>

        <div style={styles.panel}>
          <h2 style={styles.panelTitle}>Phone</h2>

          <div style={styles.callDisplay}>
            {callState === 'idle' && (
              <div style={styles.comboboxWrap}>
                <input
                  ref={dialInputRef}
                  type="text"
                  list="recent-numbers"
                  placeholder="Enter a number to call"
                  value={dialNumber}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  style={styles.combobox}
                  disabled={regState !== 'registered'}
                  autoComplete="off"
                />
                <datalist id="recent-numbers">
                  {recentNumbers.map((num) => (
                    <option key={num} value={num} />
                  ))}
                </datalist>
                {recentNumbers.length > 0 && (
                  <span style={styles.recentHint}>Recent: {recentNumbers.slice(0, 5).join(', ')}</span>
                )}
              </div>
            )}
            {callState === 'calling' && (
              <div style={styles.callInfo}>
                <span style={styles.callNumber}>{dialNumber}</span>
                <span style={styles.callStatus}>Calling...</span>
              </div>
            )}
            {callState === 'ringing' && (
              <div style={styles.callInfo}>
                <span style={styles.callNumber}>{incomingNumber}</span>
                <span style={styles.callStatus}>Incoming Call...</span>
              </div>
            )}
            {callState === 'connected' && (
              <div style={styles.callInfo}>
                <span style={styles.callNumber}>{dialNumber || incomingNumber}</span>
                <span style={styles.callStatus}>{formatDuration(callDuration)}</span>
              </div>
            )}
            {isHeld && callState === 'connected' && (
              <span style={styles.holdBadge}>HOLD</span>
            )}
          </div>

          <div style={styles.dialpad}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
              <button
                key={digit}
                onClick={() => handleDigitClick(digit)}
                style={styles.dialpadBtn}
                disabled={regState !== 'registered'}
              >
                {digit}
              </button>
            ))}
          </div>

          <div style={styles.actions}>
            {callState === 'idle' && (
              <button
                onClick={handleCall}
                style={styles.btnCall}
                disabled={!dialNumber || regState !== 'registered'}
              >
                &#x260E; Call
              </button>
            )}
            {(callState === 'calling' || callState === 'connected') && (
              <button onClick={handleHangup} style={styles.btnHangup}>
                &#x2716; Hangup
              </button>
            )}
            {callState === 'ringing' && (
              <>
                <button onClick={handleAnswer} style={styles.btnAnswer}>
                  &#x2714; Answer
                </button>
                <button onClick={handleDecline} style={styles.btnDecline}>
                  &#x2716; Decline
                </button>
              </>
            )}
          </div>

          {isCallActive && (
            <div style={styles.callActions}>
              <button
                onClick={handleHold}
                style={isHeld ? styles.btnActive : styles.btnSecondary}
              >
                {isHeld ? 'Resume' : 'Hold'}
              </button>
              <button
                onClick={handleMute}
                style={isMuted ? styles.btnActive : styles.btnSecondary}
              >
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
              <span style={styles.dtmfHint}>Press digits to send DTMF</span>
            </div>
          )}

          {callState === 'idle' && (
            <div style={styles.secondaryActions}>
              <button onClick={handleClear} style={styles.btnSmall}>Clear</button>
              <button onClick={handleBackspace} style={styles.btnSmall}>&#x232B;</button>
            </div>
          )}
        </div>
      </div>

      {recentCalls.length > 0 && (
        <div style={{ marginTop: '1.5rem', backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151' }}>
          <h3 style={{ color: '#60a5fa', margin: '0 0 1rem 0', fontWeight: '600' }}>Recent Calls</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Destination</th>
                  <th style={styles.th}>Direction</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Duration</th>
                  <th style={styles.th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.slice(0, 10).map((cdr, i) => (
                  <tr key={cdr._id || i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={styles.td}>{cdr.cdr_record?.destination?.dst || '-'}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: cdr.cdr_record?.metrics?.direction === 'outbound' ? 'rgba(243, 156, 18, 0.2)' : 'rgba(52, 152, 219, 0.2)',
                        color: cdr.cdr_record?.metrics?.direction === 'outbound' ? '#f39c12' : '#3498db',
                      }}>
                        {cdr.cdr_record?.metrics?.direction === 'outbound' ? 'Outbound' : cdr.cdr_record?.metrics?.direction === 'inbound' ? 'Inbound' : 'Internal'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor:
                          cdr.cdr_record?.metrics?.disposition === 'ANSWERED' ? 'rgba(39, 174, 96, 0.2)' :
                          cdr.cdr_record?.metrics?.disposition === 'NO ANSWER' ? 'rgba(243, 156, 18, 0.2)' :
                          'rgba(231, 76, 60, 0.2)',
                        color:
                          cdr.cdr_record?.metrics?.disposition === 'ANSWERED' ? '#27ae60' :
                          cdr.cdr_record?.metrics?.disposition === 'NO ANSWER' ? '#f39c12' :
                          '#e74c3c',
                      }}>
                        {cdr.cdr_record?.metrics?.disposition === 'ANSWERED' ? 'Answered' :
                         cdr.cdr_record?.metrics?.disposition === 'NO ANSWER' ? 'No Answer' :
                         cdr.cdr_record?.metrics?.disposition === 'BUSY' ? 'Busy' :
                         cdr.cdr_record?.metrics?.disposition === 'FAILED' ? 'Failed' :
                         cdr.cdr_record?.metrics?.disposition || 'Unknown'}
                      </span>
                    </td>
                    <td style={styles.td}>{cdr.cdr_record?.metrics?.duration ? `${Math.floor(cdr.cdr_record.metrics.duration / 60)}m ${cdr.cdr_record.metrics.duration % 60}s` : '-'}</td>
                    <td style={styles.td}>{cdr.cdr_record?.timestamps?.start ? new Date(cdr.cdr_record.timestamps.start).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <audio ref={remoteAudioRef} autoPlay />
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '1rem',
    position: 'relative',
  },
  statusIndicator: {
    position: 'absolute',
    top: '1.2rem',
    right: '1.2rem',
    zIndex: 10,
  },
  pageTitle: {
    color: '#60a5fa',
    fontWeight: '700',
    textShadow: '0 0 10px rgba(96, 165, 250, 0.3)',
    margin: '0 0 1.5rem 0',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
  },
  panel: {
    backgroundColor: '#1f2937',
    padding: '1.5rem',
    borderRadius: '12px',
    border: '1px solid #374151',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  panelTitle: {
    color: '#60a5fa',
    margin: '0 0 0.5rem 0',
    fontSize: '1.1rem',
    fontWeight: '600',
    borderBottom: '1px solid #374151',
    paddingBottom: '0.75rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
  },
  label: {
    fontWeight: '500',
    color: '#9ca3af',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    padding: '0.6rem 0.75rem',
    border: '2px solid #374151',
    borderRadius: '8px',
    fontSize: '14px',
    background: '#111827',
    color: '#e2e8f0',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#111827',
    borderRadius: '8px',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusText: {
    color: '#9ca3af',
    fontSize: '13px',
  },
  btnPrimary: {
    backgroundColor: '#60a5fa',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1rem',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
  btnDanger: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1rem',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
  callDisplay: {
    backgroundColor: '#111827',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    textAlign: 'center',
    minHeight: '60px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  comboboxWrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
  },
  combobox: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '2px solid #374151',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: '600',
    letterSpacing: '2px',
    background: '#1f2937',
    color: '#e2e8f0',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    textAlign: 'center',
  },
  recentHint: {
    color: '#6b7280',
    fontSize: '11px',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  callInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  callNumber: {
    color: '#e2e8f0',
    fontSize: '1.4rem',
    fontWeight: '600',
    letterSpacing: '2px',
  },
  callStatus: {
    color: '#9ca3af',
    fontSize: '0.85rem',
  },
  holdBadge: {
    position: 'absolute',
    top: '4px',
    right: '8px',
    backgroundColor: '#f39c12',
    color: '#111827',
    fontSize: '10px',
    fontWeight: '700',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  dialpad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
  },
  dialpadBtn: {
    padding: '0.9rem',
    border: '2px solid #374151',
    borderRadius: '8px',
    fontSize: '1.2rem',
    fontWeight: '600',
    background: '#111827',
    color: '#e2e8f0',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
  },
  btnCall: {
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    flex: '1',
    maxWidth: '160px',
  },
  btnHangup: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    flex: '1',
    maxWidth: '160px',
  },
  btnAnswer: {
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    flex: '1',
    maxWidth: '140px',
  },
  btnDecline: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    flex: '1',
    maxWidth: '140px',
  },
  callActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  btnSecondary: {
    backgroundColor: '#374151',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1.2rem',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
    flex: '1',
    maxWidth: '100px',
  },
  btnActive: {
    backgroundColor: '#f39c12',
    color: '#111827',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1.2rem',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
    flex: '1',
    maxWidth: '100px',
  },
  dtmfHint: {
    color: '#6b7280',
    fontSize: '11px',
    textAlign: 'center',
    width: '100%',
  },
  secondaryActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.75rem',
  },
  btnSmall: {
    backgroundColor: '#374151',
    color: '#e2e8f0',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: '#9ca3af',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 16px',
    color: '#e2e8f0',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
};

export default WebPhone;
